import React, { useState, useEffect, useContext, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, AlertCircle } from "lucide-react";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { useProfile } from "../../hooks/useProfile";
import { useAuth } from "../../context/AuthContext";
import AlertModal from "../../components/AlertModal";
import { getDisplayError } from "../../utils/displayError";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";

import { getStyles } from "./styles/leaveStyles";
import translations from "./constants/translations";
import { useLeaveRequests } from "./hooks/useLeaveRequests";
import { useEmployeeLeaves } from "./hooks/useEmployeeLeaves";
import { useHolidays } from "./hooks/useHolidays";
import {
  getStatusColor,
  getLeaveActionPermissions,
  getCurrentViewType,
} from "./utils/leaveUtils";
import {
  normalizeStatusValue,
  normalizeLeaveTypeValue,
} from "./utils/normalizeUtils";

import StatsGrid from "./components/StatsGrid";
import FiltersBar from "./components/FiltersBar";
import LeaveRequestItem from "./components/LeaveRequestItem";
import LeaveDetailsModal from "./components/modals/LeaveDetailsModal";
import ManageEmployeeLeavesModal from "./components/modals/ManageEmployeeLeavesModal";
import HolidayModal from "./components/modals/HolidayModal";

const LeaveApprovalPage = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();
  const { hasFeatureAccess } = useAuth();
  const t = translations[language];
  const isRTL = language === "ar";
  const styles = getStyles(theme, isRTL);

  const [userRole, setUserRole] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [filterStatus, setFilterStatus] = useState("pending");
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [alert, setAlert] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
  });

  // Modal visibility
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showManageLeaveModal, setShowManageLeaveModal] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [decision, setDecision] = useState("");
  const [comments, setComments] = useState("");

  useEffect(() => {
    const role = profile?.role || localStorage.getItem("role");
    if (role) setUserRole(role.toLowerCase());
  }, [profile]);

  const {
    leaveRequests,
    filteredRequests,
    wfhRequests,
    stats,
    isLoading,
    isAutoRefreshing,
    isSubmitting,
    error,
    lastRefresh,
    fetchLeaveRequests,
    handleSubmitDecision,
  } = useLeaveRequests({
    profile,
    language,
    theme,
    filterStatus,
    filterType,
    searchTerm,
    decision,
    comments,
    selectedLeave,
    onDecisionSuccess: () => {
      setShowDetailsModal(false);
      setDecision("");
      setComments("");
      setSelectedLeave(null);
    },
    onAlert: setAlert,
  });

  const employeeLeavesHook = useEmployeeLeaves({
    profile,
    language,
    t,
    onAlert: setAlert,
  });
  const holidaysHook = useHolidays({ profile, language, t, onAlert: setAlert });

  // ── Skeleton فقط في أول تحميل — مش هيظهر تاني مع كل ريفريش ─────────────────
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isLoading) setPageReady(true);
  }, [isLoading]);

  // Open notification target leave automatically — supports both the legacy
  // window.__notificationTarget mechanism and the notification deep-link
  // query param (?leaveId=...) set by NotificationDropdown.
  const [searchParams] = useSearchParams();
  const focusConsumedRef = useRef(false);
  useEffect(() => {
    const target = window.__notificationTarget;
    const focusLeaveId = target?.leaveId
      || (!focusConsumedRef.current ? searchParams.get("leaveId") : null);
    if (focusLeaveId && leaveRequests.length > 0) {
      const leave = leaveRequests.find((l) => l.id === Number(focusLeaveId));
      if (leave) {
        setSelectedLeave(leave);
        setShowDetailsModal(true);
        setDecision("");
        setComments("");
        window.__notificationTarget = null;
        focusConsumedRef.current = true;
      }
    }
  }, [leaveRequests, searchParams]);

  // Holiday-calendar notification deep-link (?holidayId=...) — open the
  // Holiday management modal where the change can be reviewed.
  useEffect(() => {
    if (searchParams.get("holidayId") && !focusConsumedRef.current) {
      focusConsumedRef.current = true;
      holidaysHook.openHolidayModal();
      setShowHolidayModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openManageLeaveModal = () => {
    employeeLeavesHook.resetModal();
    setShowManageLeaveModal(true);
  };

  const openHolidayModal = () => {
    holidaysHook.openHolidayModal();
    setShowHolidayModal(true);
  };

  const getStatusText = (status) => {
    const map = {
      pending: t.pending,
      approved: t.approved,
      rejected: t.rejected,
    };
    return map[status] || status;
  };

  const getLeaveTypeText = (type) => {
    const map = {
      annual: t.annual,
      sick: t.sick,
      maternity: t.maternity,
      emergency: t.emergency,
      wfh: t.wfh,
    };
    return map[type] || type;
  };

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && isLoading) {
    return (
      <div style={{ direction: isRTL ? "rtl" : "ltr" }}>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل طلبات الإجازات..." : "Loading leave requests..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Header skeleton */}
        <div
          style={{
            background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
            padding: "32px",
            borderRadius: "24px",
            border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
            marginBottom: "22px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "24px",
          }}
        >
          <div>
            <SkeletonBox width="260px" height="30px" style={{ marginBottom: "12px" }} />
            <SkeletonBox width="320px" height="18px" />
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <SkeletonBox width="170px" height="44px" style={{ borderRadius: "12px" }} />
            <SkeletonBox width="190px" height="44px" style={{ borderRadius: "12px" }} />
          </div>
        </div>

        {/* Stats skeleton */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
            gap: "clamp(16px, 3vw, 24px)",
            marginBottom: "32px",
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatCard key={i} cardStyle={styles.statCard} />
          ))}
        </div>

        {/* Filters skeleton */}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px" }}>
          <SkeletonBox width="240px" height="42px" style={{ borderRadius: "10px" }} />
          <SkeletonBox width="140px" height="42px" style={{ borderRadius: "10px" }} />
          <SkeletonBox width="110px" height="42px" style={{ borderRadius: "999px" }} />
          <SkeletonBox width="110px" height="42px" style={{ borderRadius: "999px" }} />
          <SkeletonBox width="110px" height="42px" style={{ borderRadius: "999px" }} />
        </div>

        {/* Requests grid skeleton */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "repeat(2, 1fr)",
            gap: "clamp(16px, 3vw, 24px)",
          }}
        >
          {[1, 2].map((col) => (
            <div key={col} style={styles.statCard}>
              <SkeletonBox width="180px" height="24px" style={{ marginBottom: "24px" }} />
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {[1, 2, 3].map((i) => (
                  <SkeletonBox key={i} width="100%" height="72px" style={{ borderRadius: "12px" }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ── Header ── */}
      <style>{`
        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <div className="nx-page-header"
        style={{
          background:
            theme === "dark"
              ? "rgba(255,255,255,0.02)"
              : "rgba(255,255,255,0.9)",
          padding: "32px",
          borderRadius: "24px",
          border:
            theme === "dark"
              ? "1px solid rgba(255,255,255,0.15)"
              : "1px solid rgba(0,0,0,0.05)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow:
            theme === "dark"
              ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 8px 32px rgba(0,0,0,0.1)",
          animation: "slideInDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
          transition: "all 0.3s ease",
          marginBottom: "22px",
          marginTop: "-36px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "24px",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        <div>
          <h1
            className="nx-page-title"
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: 700,
              color: theme === "dark" ? "#f1f5f9" : "#1e293b",
              letterSpacing: "-0.5px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <FileText size={28} />
            {t.leaveManagement}
          </h1>
          <p
            className="nx-page-subtitle"
            style={{
              margin: "12px 0 0 0",
              fontSize: "15px",
              fontWeight: 500,
              color: theme === "dark" ? "#cbd5e1" : "#64748b",
            }}
          >
            {t.reviewAndApprove}
          </p>
        </div>
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {hasFeatureAccess("leaves", "add") && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openHolidayModal();
              }}
              style={{
                background: "linear-gradient(135deg, #059669, #047857)",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "12px 20px",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                //boxShadow: "0 4px 16px rgba(5,150,105,0.35)",
                //transition: "all 0.3s ease",
                //whiteSpace: "nowrap",
              }}
             
            >
              {t.holidayManagement}
            </button>
          )}
          {(hasFeatureAccess("leaves", "createleave") || userRole === "manager") && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openManageLeaveModal();
              }}
              style={{
                background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "12px 20px",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                //boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.24)",
               // transition: "all 0.3s ease",
                //whiteSpace: "nowrap",
              }}
              
            >
              {t.manageEmployeeLeaves}
            </button>
          )}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={styles.errorAlert}>
          <AlertCircle size={24} />
          <div style={{ textAlign: isRTL ? "right" : "left" }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {t.errorLoadingRequests}
            </div>
            <div style={{ fontSize: 14, opacity: 0.9 }}>{getDisplayError(error, language, { kind: "load" })}</div>
          </div>
        </div>
      )}

      {/* ── Stats ── */}
      <StatsGrid
        stats={stats}
        lastRefresh={lastRefresh}
        isAutoRefreshing={isAutoRefreshing}
        theme={theme}
        language={language}
        isRTL={isRTL}
        styles={styles}
        getStatusColor={getStatusColor}
        t={t}
      />

      {/* ── Tabs + Filters ── */}
      <FiltersBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterType={filterType}
        onFilterTypeChange={setFilterType}
        isLoading={isLoading}
        filteredCount={filteredRequests.length}
        totalCount={leaveRequests.length}
        onRefresh={fetchLeaveRequests}
        theme={theme}
        language={language}
        isRTL={isRTL}
        styles={styles}
        t={t}
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          setFilterStatus(tab);
        }}
      />

      {/* ── Requests Grid ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            window.innerWidth < 768 ? "1fr" : "repeat(2, 1fr)",
          gap: "clamp(16px, 3vw, 24px)",
        }}
      >
        {/* Leave Requests */}
        <div style={styles.statCard}>
          <h3 style={{ ...styles.controlsTitle, marginBottom: 24 }}>
            <FileText size={24} />
            {t.leaveRequests} ({filteredRequests.length})
          </h3>
          {isLoading ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 40px",
                color:
                  theme === "dark"
                    ? "rgba(255,255,255,0.6)"
                    : "rgba(26,26,26,0.5)",
              }}
            >
              <div
                className="spin"
                style={{
                  width: 48,
                  height: 48,
                  border: "3px solid rgba(46,124,186,0.3)",
                  borderTopColor: "#2e7cba",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                }}
              />
              {t.loadingRequests}
            </div>
          ) : filteredRequests.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 40px",
                color:
                  theme === "dark"
                    ? "rgba(255,255,255,0.6)"
                    : "rgba(26,26,26,0.5)",
              }}
            >
              <FileText
                size={64}
                style={{ opacity: 0.3, margin: "0 auto 16px" }}
              />
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                {t.noRequestsFound}
              </h3>
              <p style={{ fontSize: 14 }}>{t.tryAdjusting}</p>
            </div>
          ) : (
            filteredRequests.map((leave) => (
              <LeaveRequestItem
                key={leave.id}
                leave={leave}
                theme={theme}
                language={language}
                isRTL={isRTL}
                styles={styles}
                getStatusColor={getStatusColor}
                getStatusText={getStatusText}
                getLeaveTypeText={getLeaveTypeText}
                t={t}
                onClick={() => {
                  setSelectedLeave(leave);
                  setShowDetailsModal(true);
                  setDecision("");
                  setComments("");
                }}
              />
            ))
          )}
        </div>

        {/* WFH Requests */}
        <div style={styles.statCard}>
          <h3 style={{ ...styles.controlsTitle, marginBottom: 24 }}>
            {t.wfh} ({wfhRequests.length})
          </h3>
          {isLoading ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 40px",
                color:
                  theme === "dark"
                    ? "rgba(255,255,255,0.6)"
                    : "rgba(26,26,26,0.5)",
              }}
            >
              <div
                className="spin"
                style={{
                  width: 48,
                  height: 48,
                  border: "3px solid rgba(46,124,186,0.3)",
                  borderTopColor: "#2e7cba",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                }}
              />
              {t.loadingRequests}
            </div>
          ) : wfhRequests.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 40px",
                color:
                  theme === "dark"
                    ? "rgba(255,255,255,0.6)"
                    : "rgba(26,26,26,0.5)",
              }}
            >
              <FileText
                size={64}
                style={{ opacity: 0.3, margin: "0 auto 16px" }}
              />
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                {language === "ar"
                  ? "لا توجد طلبات عمل من المنزل"
                  : "No work from home requests"}
              </h3>
              <p style={{ fontSize: 14 }}>{t.tryAdjusting}</p>
            </div>
          ) : (
            wfhRequests.map((leave) => (
              <LeaveRequestItem
                key={leave.id}
                leave={leave}
                theme={theme}
                language={language}
                isRTL={isRTL}
                styles={styles}
                getStatusColor={getStatusColor}
                getStatusText={getStatusText}
                getLeaveTypeText={getLeaveTypeText}
                t={t}
                onClick={() => {
                  setSelectedLeave(leave);
                  setShowDetailsModal(true);
                  setDecision("");
                  setComments("");
                }}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {showDetailsModal && selectedLeave && (
        <LeaveDetailsModal
          selectedLeave={selectedLeave}
          decision={decision}
          onDecisionChange={setDecision}
          comments={comments}
          onCommentsChange={setComments}
          isSubmitting={isSubmitting}
          theme={theme}
          language={language}
          isRTL={isRTL}
          styles={styles}
          t={t}
          profile={profile}
          userRole={userRole}
          getStatusColor={getStatusColor}
          getStatusText={getStatusText}
          getLeaveTypeText={getLeaveTypeText}
          getLeaveActionPermissions={getLeaveActionPermissions}
          getCurrentViewType={getCurrentViewType}
          onClose={() => {
            setShowDetailsModal(false);
            setDecision("");
            setComments("");
          }}
          onSubmit={handleSubmitDecision}
        />
      )}

      <ManageEmployeeLeavesModal
        show={showManageLeaveModal}
        onClose={() => setShowManageLeaveModal(false)}
        step={employeeLeavesHook.empModalStep}
        onStepChange={(step) => {
          if (step === "select") {
            employeeLeavesHook.goBackToSelect?.();
          }
        }}
        employees={employeeLeavesHook.empModalEmployees}
        empLoading={employeeLeavesHook.empModalLoading}
        empSearch={employeeLeavesHook.empModalSearch}
        onEmpSearchChange={employeeLeavesHook.setEmpModalSearch}
        onSelectEmployee={(employee) => {
          if (employee) {
            employeeLeavesHook.selectEmployee(employee);
          } else {
            employeeLeavesHook.goBackToSelect();
          }
        }}
        selectedEmployee={employeeLeavesHook.selectedEmpForLeave}
        empLeaves={employeeLeavesHook.empLeaves}
        empLeavesLoading={employeeLeavesHook.empLeavesLoading}
        deletingLeaveIds={employeeLeavesHook.deletingLeaveIds}
        onDeleteLeave={employeeLeavesHook.handleDeleteEmpLeave}
        newLeaveForm={employeeLeavesHook.newLeaveForm}
        onLeaveFormChange={employeeLeavesHook.handleLeaveFormChange}
        empQuota={employeeLeavesHook.empQuota}
        empQuotaLoading={employeeLeavesHook.empQuotaLoading}
        createLeaveLoading={employeeLeavesHook.createLeaveLoading}
        onCreateLeave={employeeLeavesHook.handleCreateEmpLeave}
        profile={profile}
        theme={theme}
        language={language}
        isRTL={isRTL}
        styles={styles}
        t={t}
        getStatusColor={getStatusColor}
        getStatusText={getStatusText}
        getLeaveTypeText={getLeaveTypeText}
        normalizeStatusValue={normalizeStatusValue}
        normalizeLeaveTypeValue={normalizeLeaveTypeValue}
      />

<HolidayModal
  show={showHolidayModal}
  onClose={() => setShowHolidayModal(false)}
  theme={theme}
  isRTL={isRTL}
  language={language}
  t={t}
  holidayForm={holidaysHook.holidayForm}
  handleHolidayFormChange={holidaysHook.handleHolidayFormChange}
  editingHoliday={holidaysHook.editingHoliday}
  handleCancelEditHoliday={holidaysHook.handleCancelEditHoliday}
  holidayCompanies={holidaysHook.holidayCompanies}
  handleSubmitHoliday={holidaysHook.handleSubmitHoliday}
  holidaySubmitting={holidaysHook.holidaySubmitting}
  holidaySearch={holidaysHook.holidaySearch}
  setHolidaySearch={holidaysHook.setHolidaySearch}
  holidayClientFilter={holidaysHook.holidayClientFilter}
  setHolidayClientFilter={holidaysHook.setHolidayClientFilter}
  holidayCompanyFilter={holidaysHook.holidayCompanyFilter}
  setHolidayCompanyFilter={holidaysHook.setHolidayCompanyFilter}
  holidayClientsList={holidaysHook.holidayClientsList}
  holidayList={holidaysHook.holidayList}
  holidayLoading={holidaysHook.holidayLoading}
  holidayDeleteConfirmId={holidaysHook.holidayDeleteConfirmId}
  setHolidayDeleteConfirmId={holidaysHook.setHolidayDeleteConfirmId}
  handleStartEditHoliday={holidaysHook.handleStartEditHoliday}
  handleDeleteHoliday={holidaysHook.handleDeleteHoliday}
  holidayDepartments={holidaysHook.holidayDepartments}
  deptPickerOpen={holidaysHook.deptPickerOpen}
  handleOpenDeptPicker={holidaysHook.handleOpenDeptPicker}
  handleCloseDeptPicker={holidaysHook.handleCloseDeptPicker}
  handleDeptPickerConfirm={holidaysHook.handleDeptPickerConfirm}
/>

      {/* ── Global Styles ── */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .spin { animation: spin 1s linear infinite; }
        .hover-card:hover { transform: translateY(-4px); box-shadow: ${theme === "dark" ? "0 12px 40px rgba(0,0,0,0.4)" : "0 12px 40px rgba(0,0,0,0.15)"} !important; }
        .request-item:hover { background: ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)"} !important; transform: translateX(${isRTL ? "-4px" : "4px"}); }
        .enhanced-input:focus { border-color: #2e7cba !important; box-shadow: 0 0 0 3px rgba(46,124,186,0.15) !important; }
        .tab-button:hover { background: ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: ${theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}; border-radius: 10px; }
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        input::placeholder, textarea::placeholder { color: ${theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(26,26,26,0.5)"}; }
        @media (max-width: 768px) { .request-item:hover { transform: none; } }
      `}</style>

      <AlertModal
        open={alert.open}
        title={alert.title}
        message={alert.message}
        variant={alert.variant}
        onClose={() => setAlert({ ...alert, open: false })}
        theme={theme}
        language={language}
      />
    </div>
  );
};

export default LeaveApprovalPage;
